import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMatchday, useSubmitPrediction, type MatchInfo } from '../../features/matchdays/api';
import { useAnnouncer } from '../../components/LiveAnnouncer';
import { usePageTitle } from '../../lib/usePageTitle';
import { formatDateTimeFull, formatKickoff, isTimeTBD } from '../../lib/dates';
import { AppLayout } from '../../components/AppLayout';
import { Button } from '../../components/Button';
import { Crest } from '../../components/Crest';
import { ApiError } from '../../lib/api';

/**
 * Inserimento pronostici (lineeguida §27, §49) — la pagina più importante
 * dell'app, costruita screen-reader-first:
 * - ogni partita è un fieldset con radiogroup semantico 1/X/2 + doppie
 * - ogni selezione viene annunciata ("Vittoria Inter selezionata")
 * - contatore di completamento sempre aggiornato
 * - deadline come testo assoluto, niente countdown che spamma
 * - riepilogo completo prima della conferma
 */

type PickCode = 'HOME' | 'DRAW' | 'AWAY' | 'HOME_DRAW' | 'DRAW_AWAY' | 'HOME_AWAY';

const PICK_OPTIONS: Array<{ code: PickCode; short: string; describe: (m: MatchInfo) => string }> = [
  { code: 'HOME', short: '1', describe: (m) => `vittoria ${m.homeTeam.shortName}` },
  { code: 'DRAW', short: 'X', describe: () => 'pareggio' },
  { code: 'AWAY', short: '2', describe: (m) => `vittoria ${m.awayTeam.shortName}` },
  { code: 'HOME_DRAW', short: '1X', describe: (m) => `doppia: ${m.homeTeam.shortName} o pareggio` },
  { code: 'DRAW_AWAY', short: 'X2', describe: (m) => `doppia: pareggio o ${m.awayTeam.shortName}` },
  { code: 'HOME_AWAY', short: '12', describe: (m) => `doppia: ${m.homeTeam.shortName} o ${m.awayTeam.shortName}` },
];

/** Le doppie stanno dietro un bottoncino per non affollare la schedina. */
const DOUBLE_CODES: PickCode[] = ['HOME_DRAW', 'DRAW_AWAY', 'HOME_AWAY'];

interface FormState {
  picks: Record<string, PickCode>; // matchId → segno
  exactMatchId: string;
  exactHome: string;
  exactAway: string;
  overMatchId: string;
}

export function PredictionPage() {
  const { id = '', mdId = '' } = useParams();
  usePageTitle('Fai i pronostici');
  const navigate = useNavigate();
  const { announce } = useAnnouncer();
  const query = useMatchday(id, mdId);
  const submit = useSubmitPrediction(id, mdId);

  const existing = query.data?.myPrediction;
  const [form, setForm] = useState<FormState | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [showSummary, setShowSummary] = useState(false);
  const [showDoubles, setShowDoubles] = useState<Record<string, boolean>>({});

  // Nei cambi di vista (riepilogo ↔ modifica) il focus va sul titolo della
  // nuova vista, come nel wizard di creazione lega — mai perso sul body.
  const editHeadingRef = useRef<HTMLHeadingElement>(null);
  const summaryHeadingRef = useRef<HTMLHeadingElement>(null);
  const summaryToggled = useRef(false);
  useEffect(() => {
    if (!summaryToggled.current) return; // non rubare il focus al primo render
    (showSummary ? summaryHeadingRef : editHeadingRef).current?.focus();
  }, [showSummary]);

  // Bozza locale: su mobile le interruzioni sono la norma (notifica, cambio
  // app, tab uccisa dal sistema) — i tap investiti non si devono perdere.
  const draftKey = `pc:bozza:${id}:${mdId}`;
  const [draft] = useState<FormState | null>(() => {
    try {
      const raw = localStorage.getItem(draftKey);
      return raw ? (JSON.parse(raw) as FormState) : null;
    } catch {
      return null;
    }
  });
  useEffect(() => {
    if (!form) return;
    try {
      localStorage.setItem(draftKey, JSON.stringify(form));
    } catch {
      // storage non disponibile (es. navigazione privata): pazienza
    }
  }, [form, draftKey]);

  // Inizializza dalla schedina già inviata, altrimenti dalla bozza locale.
  const state: FormState = form ??
    (existing
      ? {
          picks: Object.fromEntries(existing.items.map((i) => [i.matchId, i.pick as PickCode])),
          exactMatchId: existing.exact?.matchId ?? '',
          exactHome: existing.exact != null ? String(existing.exact.home) : '',
          exactAway: existing.exact != null ? String(existing.exact.away) : '',
          overMatchId: existing.overMatchId ?? '',
        }
      : draft) ?? {
      picks: {},
      exactMatchId: '',
      exactHome: '',
      exactAway: '',
      overMatchId: '',
    };
  const draftRestored = !existing && !!draft && Object.keys(draft.picks).length > 0;

  const matchday = query.data?.matchday;
  const matches = matchday?.matches ?? [];
  const matchById = useMemo(() => new Map(matches.map((m) => [m.id, m])), [matches]);
  // Regole dal ruleset della giornata (configurabile per lega).
  const FIXED_COUNT = query.data?.rules.fixedCount ?? 4;
  const allowDouble = query.data?.rules.allowDouble ?? true;
  const pickOptions = allowDouble ? PICK_OPTIONS : PICK_OPTIONS.filter((o) => o.code === 'HOME' || o.code === 'DRAW' || o.code === 'AWAY');

  // Valore in punti di ogni segno per QUESTA partita (dalla matrice fasce).
  // L'originalità resta segreta: qui si mostrano solo i punti base.
  const tierMatrix = query.data?.rules.tierMatrix;
  const doubleDivisor = query.data?.rules.doubleDivisor ?? 2;
  const fmtPts = (n: number) =>
    new Intl.NumberFormat('it-IT', { maximumFractionDigits: 2 }).format(n);
  const pickValue = (match: MatchInfo, code: PickCode): { text: string; sr: string } | null => {
    const cell = tierMatrix?.[`${match.homeTier ?? 2}-${match.awayTier ?? 2}`];
    if (!cell) return null;
    const base: Record<PickCode, number[]> = {
      HOME: [cell.home],
      DRAW: [cell.draw],
      AWAY: [cell.away],
      HOME_DRAW: [cell.home, cell.draw],
      DRAW_AWAY: [cell.draw, cell.away],
      HOME_AWAY: [cell.home, cell.away],
    };
    const isDouble = base[code].length === 2;
    const points = base[code].map((v) =>
      isDouble ? Math.round((v / doubleDivisor) * 100) / 100 : v,
    );
    const unique = [...new Set(points)].map(fmtPts);
    return {
      text: `${unique.join(' o ')} pt`,
      sr: isDouble
        ? `vale ${unique.join(' oppure ')} punti secondo il segno che esce, già dimezzati per la doppia`
        : `vale ${unique[0]} ${unique[0] === '1' ? 'punto' : 'punti'}`,
    };
  };
  const ptsWord = (n: number) => (n === 1 ? 'punto' : 'punti');

  const overRequired = query.data?.rules.overRequired ?? false;
  const pickedCount = Object.keys(state.picks).length;
  const exactComplete = !!state.exactMatchId && state.exactHome !== '' && state.exactAway !== '';
  const overChosen = !!state.overMatchId;
  const overComplete = overRequired ? overChosen : true; // facoltativo se il regolamento lo consente

  const setPick = (match: MatchInfo, code: PickCode | null) => {
    const picks = { ...state.picks };
    let announcement: string;
    if (code === null) {
      delete picks[match.id];
      announcement = `${match.homeTeam.shortName} contro ${match.awayTeam.shortName}: pronostico rimosso.`;
    } else {
      picks[match.id] = code;
      const option = PICK_OPTIONS.find((o) => o.code === code)!;
      announcement = `${option.describe(match)} selezionata per ${match.homeTeam.shortName} contro ${match.awayTeam.shortName}.`;
    }
    const next = { ...state, picks };
    // La quinta partita non può coincidere con una fissa.
    if (code !== null && next.exactMatchId === match.id) next.exactMatchId = '';
    setForm(next);
    const total = Object.keys(picks).length;
    announce(`${announcement} Partite completate: ${total} di ${FIXED_COUNT}.`);
  };

  const canConfirm = pickedCount === FIXED_COUNT && exactComplete && overComplete;

  const doSubmit = async () => {
    setServerError(null);
    try {
      const res = await submit.mutateAsync({
        items: Object.entries(state.picks).map(([matchId, pick]) => ({ matchId, pick })),
        exact: {
          matchId: state.exactMatchId,
          home: Number(state.exactHome),
          away: Number(state.exactAway),
        },
        ...(state.overMatchId ? { overMatchId: state.overMatchId } : {}),
      });
      announce(res.message, 'assertive');
      try {
        localStorage.removeItem(draftKey); // schedina sul server: bozza non più necessaria
      } catch {
        // ignora
      }
      navigate(`/leghe/${id}/giornate/${mdId}`);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Qualcosa è andato storto. Riprova.';
      setServerError(message);
      setShowSummary(false);
      announce(`Errore: ${message}`, 'assertive');
    }
  };

  if (query.isLoading) {
    return (
      <AppLayout title="Fai i pronostici">
        <p role="status" className="mt-4">Caricamento della giornata…</p>
      </AppLayout>
    );
  }

  if (!matchday) {
    return (
      <AppLayout title="Giornata non trovata">
        <p role="alert" className="mt-4 font-medium text-danger">Questa giornata non esiste.</p>
      </AppLayout>
    );
  }

  if (matchday.state !== 'OPEN') {
    return (
      <AppLayout title={`Giornata ${matchday.number}`}>
        <p role="alert" className="mt-4 text-xl font-bold">PRONOSTICI CHIUSI</p>
        <p className="mt-2 text-ink-700 dark:text-ink-200">
          I pronostici per questa giornata non sono aperti.
        </p>
      </AppLayout>
    );
  }

  const exactCandidates = matches.filter((m) => !state.picks[m.id]);

  return (
    <AppLayout title={`Pronostici — Giornata ${matchday.number}`}>
      {matchday.deadline && (
        <p className="mt-2 text-lg text-ink-700 dark:text-ink-200">
          {matches[0] && matches.every((m) => isTimeTBD(m.kickoffAt))
            ? 'La giornata chiuderà poco prima della prima partita: gli orari ufficiali non sono ancora stati pubblicati, la chiusura si adeguerà da sola.'
            : `La giornata chiuderà ${formatDateTimeFull(matchday.deadline)}.`}
        </p>
      )}
      {existing && (
        <p className="mt-1 text-ink-700 dark:text-ink-200">
          Hai già inviato una schedina: puoi modificarla fino alla chiusura.
        </p>
      )}
      {draftRestored && (
        <p
          role="status"
          className="mt-3 rounded-xl border-2 border-panda-600 bg-panda-50 px-4 py-3 font-medium dark:border-panda-400 dark:bg-ink-900"
        >
          📝 Abbiamo ripristinato la bozza che avevi iniziato: riprendi da dove eri. Ricorda di
          confermarla, altrimenti non vale.
        </p>
      )}

      {serverError && (
        <div role="alert" className="mt-4 rounded-lg border-2 border-danger bg-red-50 p-3 font-medium text-danger dark:bg-ink-800">
          {serverError}
        </div>
      )}

      {/* Stato di completamento, sempre visibile e semantico */}
      <p className="sticky top-0 z-10 mt-6 rounded-xl border border-ink-200 bg-white/95 px-4 py-3 font-semibold backdrop-blur dark:border-ink-800 dark:bg-ink-950/95">
        Partite completate: {pickedCount}/{FIXED_COUNT}
        {' · '}Risultato esatto: {exactComplete ? '✓ fatto' : 'da fare'}
        {' · '}OVER:{' '}
        {overChosen ? '✓ scelto' : overRequired ? 'da fare' : 'facoltativo'}
      </p>

      {!showSummary && (
        <>
          {/* --- 1. Partite fisse --- */}
          <section aria-labelledby="sez-fisse" className="mt-8">
            <h2 ref={editHeadingRef} tabIndex={-1} id="sez-fisse" className="text-2xl font-bold tracking-tight">
              Scegli {FIXED_COUNT} partite
            </h2>
            <p className="mt-1 text-ink-700 dark:text-ink-200">
              Pronostico secco (1, X, 2) oppure doppia (1X, X2, 12 — la doppia dimezza i punti).
            </p>
            {pickedCount >= FIXED_COUNT && (
              <p className="mt-2 font-medium text-panda-800 dark:text-panda-300">
                Hai già scelto {FIXED_COUNT} partite: le altre sono bloccate. Per cambiarne una,
                togli prima un segno (ri-tocca il segno selezionato).
              </p>
            )}

            <div className="mt-4 flex flex-col gap-4">
              {matches.map((match) => {
                const selected = state.picks[match.id];
                const disabled = !selected && pickedCount >= FIXED_COUNT;
                return (
                  <fieldset
                    key={match.id}
                    className={`rounded-2xl border p-4 ${
                      selected
                        ? 'border-panda-600 bg-panda-50 dark:bg-ink-900 dark:border-panda-400'
                        : 'border-ink-200 dark:border-ink-800'
                    }`}
                  >
                    <legend className="px-1 font-bold">
                      <Crest url={match.homeTeam.crestUrl} className="mr-1.5" />
                      {match.homeTeam.name} — <Crest url={match.awayTeam.crestUrl} className="mx-1.5" />
                      {match.awayTeam.name}
                    </legend>
                    <p className="text-sm text-ink-600 dark:text-ink-300">
                      {formatKickoff(match.kickoffAt)}
                    </p>
                    {(() => {
                      const doublesOpen =
                        showDoubles[match.id] || (selected !== undefined && DOUBLE_CODES.includes(selected));
                      const visibleOptions = pickOptions.filter(
                        (o) => !DOUBLE_CODES.includes(o.code) || doublesOpen,
                      );
                      const renderChip = (option: (typeof PICK_OPTIONS)[number]) => {
                        const value = pickValue(match, option.code);
                        const isSelected = selected === option.code;
                        return (
                          <label
                            key={option.code}
                            className={`inline-flex min-h-11 min-w-14 cursor-pointer flex-col items-center justify-center rounded-xl border-2 px-3 py-1.5 font-semibold leading-tight ${
                              isSelected
                                ? 'border-panda-700 bg-panda-700 text-white dark:border-panda-400 dark:bg-panda-600'
                                : disabled
                                  ? 'cursor-not-allowed border-ink-200 text-ink-400 dark:border-ink-700'
                                  : 'border-ink-300 dark:border-ink-600'
                            }`}
                          >
                            <input
                              type="radio"
                              name={`pick-${match.id}`}
                              className="visually-hidden"
                              checked={isSelected}
                              disabled={disabled && !isSelected}
                              onChange={() => setPick(match, option.code)}
                              // Ricliccare il segno già scelto lo rimuove.
                              onClick={() => {
                                if (isSelected) setPick(match, null);
                              }}
                              // Da tastiera Spazio su un radio già selezionato
                              // NON genera click: la deselezione va gestita qui,
                              // altrimenti l'istruzione annunciata allo screen
                              // reader ("attiva di nuovo per rimuoverlo") è falsa.
                              onKeyDown={(e) => {
                                if (isSelected && (e.key === ' ' || e.key === 'Enter')) {
                                  e.preventDefault();
                                  setPick(match, null);
                                }
                              }}
                            />
                            <span aria-hidden="true">{option.short}</span>
                            {value && (
                              <span
                                aria-hidden="true"
                                className={`text-xs font-medium ${
                                  isSelected ? 'text-white/85' : 'text-ink-500 dark:text-ink-400'
                                }`}
                              >
                                {value.text}
                              </span>
                            )}
                            <span className="visually-hidden">
                              {option.describe(match)}
                              {value ? `, ${value.sr}` : ''}
                              {isSelected ? '. Selezionato: attiva di nuovo per rimuoverlo' : ''}
                              {disabled && !isSelected
                                ? `. Non disponibile: hai già scelto ${FIXED_COUNT} partite, togli un segno per cambiarla`
                                : ''}
                            </span>
                          </label>
                        );
                      };
                      return (
                        <div
                          className="mt-3 flex flex-wrap items-stretch gap-2"
                          role="radiogroup"
                          aria-label={`Pronostico per ${match.homeTeam.name} contro ${match.awayTeam.name}`}
                        >
                          {visibleOptions.map(renderChip)}
                          {allowDouble && (
                            <button
                              type="button"
                              aria-expanded={doublesOpen}
                              onClick={() =>
                                setShowDoubles({ ...showDoubles, [match.id]: !doublesOpen })
                              }
                              className="inline-flex min-h-11 items-center rounded-xl border-2 border-dashed border-ink-300 px-3 py-1.5 text-sm font-semibold text-ink-600 hover:border-panda-600 hover:text-panda-700 dark:border-ink-600 dark:text-ink-300 dark:hover:border-panda-400 dark:hover:text-panda-300"
                            >
                              {doublesOpen ? 'Nascondi doppie' : 'Doppie…'}
                              <span className="visually-hidden">
                                {' '}per {match.homeTeam.shortName} contro {match.awayTeam.shortName}
                                {doublesOpen ? '' : ' (1X, X2, 12 — dimezzano i punti)'}
                              </span>
                            </button>
                          )}
                        </div>
                      );
                    })()}
                  </fieldset>
                );
              })}
            </div>
          </section>

          {/* --- 2. Risultato esatto --- */}
          <section aria-labelledby="sez-esatto" className="mt-10">
            <h2 id="sez-esatto" className="text-2xl font-bold tracking-tight">
              Risultato esatto
            </h2>
            <p className="mt-1 text-ink-700 dark:text-ink-200">
              Scegli una quinta partita (diversa dalle {FIXED_COUNT} fisse) e pronostica il risultato
              esatto: se lo indovini prendi +{fmtPts(query.data?.rules.exactBonus ?? 3)} punti in
              aggiunta ai punti del segno; qualsiasi altro risultato vale zero, anche col segno
              giusto. Tutto o niente.
            </p>

            <div className="mt-4 flex flex-col gap-2" role="radiogroup" aria-label="Partita per il risultato esatto">
              {exactCandidates.map((m) => (
                <label
                  key={m.id}
                  className={`flex min-h-11 cursor-pointer items-center gap-3 rounded-xl border p-3 ${
                    state.exactMatchId === m.id
                      ? 'border-panda-600 bg-panda-50 dark:bg-ink-900 dark:border-panda-400'
                      : 'border-ink-200 dark:border-ink-800'
                  }`}
                >
                  <input
                    type="radio"
                    name="exact-match"
                    className="h-5 w-5 accent-panda-700"
                    checked={state.exactMatchId === m.id}
                    onChange={() => {
                      setForm({ ...state, exactMatchId: m.id });
                      announce(`Risultato esatto su ${m.homeTeam.shortName} contro ${m.awayTeam.shortName}.`);
                    }}
                  />
                  <span className="font-medium">
                    <Crest url={m.homeTeam.crestUrl} className="mr-1.5" />
                    {m.homeTeam.name} — <Crest url={m.awayTeam.crestUrl} className="mx-1.5" />
                    {m.awayTeam.name}
                  </span>
                </label>
              ))}
            </div>

            {state.exactMatchId && (
              <div className="mt-4 flex items-end gap-4">
                <div className="flex flex-col gap-1">
                  <label htmlFor="exact-home" className="font-medium">
                    Gol {matchById.get(state.exactMatchId)?.homeTeam.shortName ?? 'casa'}
                  </label>
                  <input
                    id="exact-home"
                    type="number"
                    inputMode="numeric"
                    min={0}
                    max={20}
                    className="w-24 rounded-lg border border-ink-300 px-3 py-2.5 text-center text-xl font-bold dark:border-ink-600 dark:bg-ink-900"
                    value={state.exactHome}
                    onChange={(e) => setForm({ ...state, exactHome: e.target.value })}
                  />
                </div>
                <span aria-hidden="true" className="pb-3 text-xl font-bold">—</span>
                <div className="flex flex-col gap-1">
                  <label htmlFor="exact-away" className="font-medium">
                    Gol {matchById.get(state.exactMatchId)?.awayTeam.shortName ?? 'ospite'}
                  </label>
                  <input
                    id="exact-away"
                    type="number"
                    inputMode="numeric"
                    min={0}
                    max={20}
                    className="w-24 rounded-lg border border-ink-300 px-3 py-2.5 text-center text-xl font-bold dark:border-ink-600 dark:bg-ink-900"
                    value={state.exactAway}
                    onChange={(e) => setForm({ ...state, exactAway: e.target.value })}
                  />
                </div>
              </div>
            )}

            {/* Anteprima punti in tempo reale mentre scrivi il risultato */}
            {(() => {
              const m = matchById.get(state.exactMatchId);
              if (!m || state.exactHome === '' || state.exactAway === '') return null;
              const h = Number(state.exactHome);
              const a = Number(state.exactAway);
              if (Number.isNaN(h) || Number.isNaN(a)) return null;
              const impliedCode: PickCode = h > a ? 'HOME' : h < a ? 'AWAY' : 'DRAW';
              const impliedShort = h > a ? '1' : h < a ? '2' : 'X';
              const cell = tierMatrix?.[`${m.homeTier ?? 2}-${m.awayTier ?? 2}`];
              if (!cell) return null;
              const basePts =
                impliedCode === 'HOME' ? cell.home : impliedCode === 'DRAW' ? cell.draw : cell.away;
              const bonus = query.data?.rules.exactBonus ?? 3;
              return (
                <p
                  role="status"
                  className="mt-3 max-w-md rounded-xl border border-panda-600 bg-panda-50 px-4 py-3 dark:border-panda-400 dark:bg-ink-900"
                >
                  Se {m.homeTeam.shortName} – {m.awayTeam.shortName} finisce{' '}
                  <strong>
                    {h}-{a}
                  </strong>{' '}
                  prendi <strong>{fmtPts(basePts + bonus)} {ptsWord(basePts + bonus)}</strong> (
                  {fmtPts(basePts)} del segno {impliedShort} + {fmtPts(bonus)} di risultato esatto).
                  Qualsiasi altro risultato: 0 punti — la quinta partita vale solo se esatta.
                </p>
              );
            })()}
          </section>

          {/* --- 3. OVER --- */}
          <section aria-labelledby="sez-over" className="mt-10">
            <h2 id="sez-over" className="text-2xl font-bold tracking-tight">
              OVER{!overRequired && <span className="ml-2 text-base font-medium text-ink-600 dark:text-ink-300">(facoltativo)</span>}
            </h2>
            <p className="mt-1 text-ink-700 dark:text-ink-200">
              Scegli la partita con più gol:{' '}
              {[...(query.data?.rules.overLevels ?? [])]
                .sort((a, b) => a.goals - b.goals)
                .map((l) => `da ${l.goals} gol +${fmtPts(l.points)}`)
                .join(', ')}
              ; sotto{' '}
              {Math.min(...(query.data?.rules.overLevels ?? [{ goals: 4, points: 3 }]).map((l) => l.goals))}{' '}
              gol {fmtPts(query.data?.rules.overPenalty ?? -2)} punti.
              {!overRequired && ' Se preferisci non rischiare, puoi anche non giocarlo.'}
            </p>
            {overChosen && !overRequired && (
              <button
                type="button"
                onClick={() => {
                  setForm({ ...state, overMatchId: '' });
                  announce('OVER rimosso: non lo giocherai in questa giornata.');
                }}
                className="mt-3 min-h-11 rounded-lg px-3 py-2 font-medium text-ink-600 underline dark:text-ink-300"
              >
                Rimuovi l’OVER
              </button>
            )}
            <div className="mt-4 flex flex-col gap-2" role="radiogroup" aria-label="Partita per l’OVER">
              {matches.map((m) => (
                <label
                  key={m.id}
                  className={`flex min-h-11 cursor-pointer items-center gap-3 rounded-xl border p-3 ${
                    state.overMatchId === m.id
                      ? 'border-panda-600 bg-panda-50 dark:bg-ink-900 dark:border-panda-400'
                      : 'border-ink-200 dark:border-ink-800'
                  }`}
                >
                  <input
                    type="radio"
                    name="over-match"
                    className="h-5 w-5 accent-panda-700"
                    checked={state.overMatchId === m.id}
                    onChange={() => {
                      setForm({ ...state, overMatchId: m.id });
                      announce(`OVER su ${m.homeTeam.shortName} contro ${m.awayTeam.shortName}.`);
                    }}
                  />
                  <span className="font-medium">
                    <Crest url={m.homeTeam.crestUrl} className="mr-1.5" />
                    {m.homeTeam.name} — <Crest url={m.awayTeam.crestUrl} className="mx-1.5" />
                    {m.awayTeam.name}
                  </span>
                </label>
              ))}
            </div>
          </section>

          <div className="mt-10">
            <Button
              onClick={() => {
                if (!canConfirm) {
                  const missing: string[] = [];
                  if (pickedCount < FIXED_COUNT)
                    missing.push(`${FIXED_COUNT - pickedCount} partite fisse`);
                  if (!exactComplete) missing.push('il risultato esatto');
                  if (overRequired && !overChosen) missing.push('l’OVER');
                  announce(`Manca ancora: ${missing.join(', ')}.`, 'assertive');
                  return;
                }
                summaryToggled.current = true;
                setShowSummary(true);
                announce('Riepilogo pronostici pronto. Controlla e conferma.');
              }}
            >
              Vai al riepilogo
            </Button>
          </div>
        </>
      )}

      {/* --- 4. Riepilogo e conferma --- */}
      {showSummary && (
        <section aria-labelledby="sez-riepilogo" className="mt-8">
          <h2 ref={summaryHeadingRef} tabIndex={-1} id="sez-riepilogo" className="text-2xl font-bold tracking-tight">
            Riepilogo pronostici
          </h2>
          <ul className="mt-4 flex flex-col divide-y divide-ink-200 rounded-2xl border border-ink-200 dark:divide-ink-800 dark:border-ink-800">
            {Object.entries(state.picks).map(([matchId, pick]) => {
              const m = matchById.get(matchId);
              const option = PICK_OPTIONS.find((o) => o.code === pick);
              if (!m || !option) return null;
              return (
                <li key={matchId} className="flex items-center justify-between gap-3 px-4 py-3">
                  <span>
                    <Crest url={m.homeTeam.crestUrl} className="mr-1.5" />
                    {m.homeTeam.name} — <Crest url={m.awayTeam.crestUrl} className="mx-1.5" />
                    {m.awayTeam.name}
                  </span>
                  <span className="font-bold">
                    {option.short}
                    <span className="visually-hidden"> ({option.describe(m)})</span>
                  </span>
                </li>
              );
            })}
            <li className="flex items-center justify-between gap-3 px-4 py-3">
              <span>
                Risultato esatto: {matchById.get(state.exactMatchId)?.homeTeam.name} —{' '}
                {matchById.get(state.exactMatchId)?.awayTeam.name}
              </span>
              <span className="font-bold">
                {state.exactHome}-{state.exactAway}
              </span>
            </li>
            <li className="flex items-center justify-between gap-3 px-4 py-3">
              {overChosen ? (
                <>
                  <span>
                    OVER: {matchById.get(state.overMatchId)?.homeTeam.name} —{' '}
                    {matchById.get(state.overMatchId)?.awayTeam.name}
                  </span>
                  <span aria-hidden="true" className="font-bold">✓</span>
                </>
              ) : (
                <span>OVER: non giocato</span>
              )}
            </li>
          </ul>

          <div className="mt-6 flex gap-3">
            <Button
              variant="secondary"
              onClick={() => {
                summaryToggled.current = true;
                setShowSummary(false);
              }}
            >
              Torna a modificare
            </Button>
            <Button onClick={doSubmit} loading={submit.isPending} loadingText="Invio in corso…">
              Conferma pronostici
            </Button>
          </div>
        </section>
      )}
    </AppLayout>
  );
}
